/**
 * 顾问陪跑 · 销售管理（草案 A-4，超管 advisor:manage 与运营主管 advisor:team_view 可见）。
 *
 * 每位销售一行：名下客户数、各阶段的书数、待跟进数、本月成交、是否绑了企业微信。
 * 超管：离职交接在这里一步到位——「转交全部」把这位销售名下的全部客户转给另一位（每位客户一条转交记录）。
 * 运营主管（没有 advisor:manage）：看不到「转交全部」与「需转交」提示；点销售行 → 右侧抽屉看这位销售的团队名册
 * （不含联系方式与内容），勾选客户 + 必填理由做受约束的转交（≤20 位 / 次，24h 累计 ≤50 位）。
 * 停用账号 / 绑定企微成员在「账号管理」里做（会话回收等安全动作不在这里重复一份），这里只给入口。
 *
 * 已停用 / 不再是销售或运营主管的账号名下还有客户时，红色标出「需转交」——这些客户没人跟进（草案 §2.4）。
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdvisorSalesOverview,
  listAdvisorSales,
  transferAllFromSales,
  type SalesOverviewRowDto,
} from '../../../api/advisor';
import { ATTENTION_META, ATTENTION_ORDER, STAGE_META, STAGE_OPTIONS, yuanText } from '../customers/stage';
import { roleLabel } from '../../../utils/role-labels';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../contracts/permissions';
import TeamRosterDrawer from './team-roster-drawer';

const { Text } = Typography;

const errText = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

/** 能「带客户」的角色：销售、运营主管（与后端 ASSIGNABLE_ROLES 同口径，另含超管） */
const CUSTOMER_OWNER_ROLES = new Set(['sales', 'ops_lead', 'super_admin']);

/** 名下还有客户、但这个账号已经不能再服务客户（已停用 / 不再是销售或运营主管且不是超管） */
export function needsTransfer(row: Pick<SalesOverviewRowDto, 'status' | 'role' | 'customerCount'>): boolean {
  if (row.customerCount === 0) return false;
  return row.status !== 'active' || !CUSTOMER_OWNER_ROLES.has(row.role);
}

/** 团队名册只对「能带客户的账号」开放（服务端同口径，其余 404）；没客户也没什么可看 */
function canOpenRoster(row: Pick<SalesOverviewRowDto, 'role' | 'customerCount'>): boolean {
  return row.customerCount > 0 && CUSTOMER_OWNER_ROLES.has(row.role);
}

export default function AdvisorSalesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // 超管（advisor:manage）：看全部、转交全部、需转交提示；其余（运营主管）走团队名册抽屉
  const canManage = usePermission(PERMISSIONS.ADVISOR_MANAGE);
  const canAdmin = usePermission(PERMISSIONS.ADMIN_READ);
  const [transferFrom, setTransferFrom] = useState<SalesOverviewRowDto | null>(null);
  const [rosterOf, setRosterOf] = useState<SalesOverviewRowDto | null>(null);
  const [form] = Form.useForm<{ toAdminId: string; reason?: string }>();

  const overview = useQuery({ queryKey: ['advisor-sales-overview'], queryFn: getAdvisorSalesOverview, retry: false });
  const salesList = useQuery({ queryKey: ['advisor-sales'], queryFn: listAdvisorSales, enabled: !!transferFrom });

  const transfer = useMutation({
    mutationFn: (v: { fromAdminId: string; toAdminId: string; reason?: string }) =>
      transferAllFromSales(v.fromAdminId, { toAdminId: v.toAdminId, reason: v.reason }),
    onSuccess: (data) => {
      const skipped = data.skipped.length ? `，${data.skipped.length} 位未转（已属于对方或已被转走）` : '';
      message.success(`已转交 ${data.transferred.length} 位客户${skipped}`);
      setTransferFrom(null);
      form.resetFields();
      // 归属变了：销售管理、首页工作台、客户列表都要重取
      for (const key of ['advisor-sales-overview', 'advisor-workbench', 'advisor-customers']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => {
      message.error(errText(e, '转交失败'));
    },
  });

  const rows = overview.data?.rows ?? [];
  // 「需转交」是超管的待办（转交全部）；运营主管不显示这类提示，只看到账号已停用等事实标签
  const flagged = (row: SalesOverviewRowDto) => canManage && needsTransfer(row);
  const orphaned = rows.filter(flagged);
  const orphanedCustomers = orphaned.reduce((sum, r) => sum + r.customerCount, 0);

  const columns: ColumnsType<SalesOverviewRowDto> = [
    {
      title: '销售',
      render: (_, row) => (
        <Space size={4} wrap>
          <Text strong>{row.nickname}</Text>
          {row.status === 'active' ? <Tag color="green">在职</Tag> : <Tag color="red">已停用</Tag>}
          {row.role !== 'sales' && <Tag>{row.role === 'super_admin' ? '超管名下' : roleLabel(row.role)}</Tag>}
          {flagged(row) && <Tag color="red">需转交</Tag>}
        </Space>
      ),
    },
    {
      title: '企业微信',
      width: 110,
      render: (_, row) =>
        row.wecomBound ? (
          <Tag color="green">已绑定</Tag>
        ) : (
          <Tooltip
            title={
              canAdmin
                ? '没绑定企业微信成员，这位销售打不开聊天侧边栏。到「账号管理」里填企微成员账号。'
                : '没绑定企业微信成员，这位销售打不开聊天侧边栏。'
            }
          >
            <Tag color={row.status === 'active' && (row.role === 'sales' || row.role === 'ops_lead') ? 'orange' : 'default'}>
              未绑定
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: '名下客户',
      width: 90,
      dataIndex: 'customerCount',
      render: (v: number, row) => <Text type={flagged(row) ? 'danger' : undefined}>{v}</Text>,
    },
    {
      title: '各阶段（书）',
      render: (_, row) => {
        const tags = STAGE_OPTIONS.filter((s) => (row.stageCounts[s] ?? 0) > 0);
        return tags.length === 0 ? (
          <Text type="secondary">-</Text>
        ) : (
          <Space size={2} wrap>
            {tags.map((s) => (
              <Tag key={s} color={STAGE_META[s].color} style={{ marginInlineEnd: 0 }}>
                {STAGE_META[s].label} {row.stageCounts[s]}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '待跟进',
      render: (_, row) => {
        const tags = ATTENTION_ORDER.filter((a) => (row.attentionCounts[a] ?? 0) > 0);
        return tags.length === 0 ? (
          <Text type="secondary">-</Text>
        ) : (
          <Space size={2} wrap>
            {tags.map((a) => (
              <Tooltip key={a} title={ATTENTION_META[a].hint}>
                <Tag color={ATTENTION_META[a].color} style={{ marginInlineEnd: 0 }}>
                  {ATTENTION_META[a].label} {row.attentionCounts[a]}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: `本月成交${overview.data ? `（${overview.data.monthLabel}）` : ''}`,
      render: (_, row) =>
        row.monthPaidCount === 0 ? (
          <Text type="secondary">-</Text>
        ) : (
          <Text>
            {row.monthPaidCount} 单 · {yuanText(row.monthPaidFen)}
          </Text>
        ),
    },
    {
      title: '',
      render: (_, row) =>
        canManage ? (
          <Space size={4} wrap>
            <Button
              size="small"
              disabled={row.customerCount === 0}
              onClick={() => navigate(`/advisor/customers?advisorAdminId=${encodeURIComponent(row.id)}`)}
            >
              查看客户
            </Button>
            <Button size="small" danger={needsTransfer(row)} disabled={row.customerCount === 0} onClick={() => setTransferFrom(row)}>
              转交全部
            </Button>
          </Space>
        ) : (
          <Button size="small" disabled={!canOpenRoster(row)} onClick={() => setRosterOf(row)}>
            团队名册
          </Button>
        ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {orphaned.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={`有 ${orphanedCustomers} 位客户没人跟进，请尽快转交`}
          description={orphaned.map((r) => `${r.nickname}（${r.status === 'active' ? '已不是销售' : '账号已停用'}）名下 ${r.customerCount} 位`).join('；')}
        />
      )}
      <Card
        title="销售管理"
        extra={canAdmin ? <Link to="/admins">账号管理（停用 / 绑定企业微信成员）</Link> : undefined}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="本月成交按客户「当前」归属的销售统计，转交后跟着客户走；已退款的不算。各阶段和待跟进按书（订单）计，给爸妈各做一本就是两本。"
        />
        <Table<SalesOverviewRowDto>
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={overview.isLoading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          // 运营主管：点整行也能打开这位销售的团队名册
          onRow={
            canManage
              ? undefined
              : (row) => ({
                  onClick: () => canOpenRoster(row) && setRosterOf(row),
                  style: canOpenRoster(row) ? { cursor: 'pointer' } : undefined,
                })
          }
          locale={{ emptyText: overview.isError ? '加载失败' : '还没有销售账号，到「账号管理」新建角色为 sales 的账号' }}
        />
      </Card>

      {!canManage && <TeamRosterDrawer sales={rosterOf} onClose={() => setRosterOf(null)} />}

      <Modal
        title={transferFrom ? `把「${transferFrom.nickname}」名下的 ${transferFrom.customerCount} 位客户全部转交` : '转交'}
        open={!!transferFrom}
        onCancel={() => setTransferFrom(null)}
        onOk={() => form.submit()}
        okText="确认转交"
        confirmLoading={transfer.isPending}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="转交后原销售立即看不到这些客户，新销售能看到全部历史（含订单与转交记录）。每位客户会各留一条转交记录。"
        />
        <Form
          name="transfer_all"
          form={form}
          layout="vertical"
          preserve={false}
          onFinish={(v) => transferFrom && transfer.mutate({ fromAdminId: transferFrom.id, toAdminId: v.toAdminId, reason: v.reason })}
        >
          <Form.Item name="toAdminId" label="接收的销售" rules={[{ required: true, message: '请选择接收人' }]}>
            <Select
              loading={salesList.isLoading}
              options={(salesList.data ?? [])
                .filter((s) => s.id !== transferFrom?.id)
                .map((s) => ({ value: s.id, label: s.nickname }))}
            />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input placeholder="如：离职交接" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
